export interface TimeSlotForOverlapCheck {
  id?: string;
  start_time: string;
  end_time: string;
  available_days: number[];
}

/**
 * Vérifie si deux plages horaires se chevauchent
 * (startA < endB) AND (startB < endA)
 */
export function hasTimeOverlap(startA: string, endA: string, startB: string, endB: string): boolean {
  return startA < endB && startB < endA;
}

/**
 * Vérifie si deux tableaux de jours ont des jours en commun
 * - Si l'un des deux est vide (tous les jours), chevauchement certain
 * - Sinon, vérifier l'intersection
 */
export function hasDaysOverlap(daysA: number[], daysB: number[]): boolean {
  // Si l'un des deux est vide (tous les jours), chevauchement certain
  if (daysA.length === 0 || daysB.length === 0) return true;
  // Sinon, vérifier intersection
  return daysA.some(day => daysB.includes(day));
}

/**
 * Trouve tous les créneaux qui chevauchent le créneau donné
 * @param newSlot Le créneau à vérifier (nouveau ou en cours de modification)
 * @param existingSlots Les créneaux existants à comparer
 * @returns Les créneaux en conflit
 */
export function findOverlappingSlots(
  newSlot: TimeSlotForOverlapCheck,
  existingSlots: TimeSlotForOverlapCheck[]
): TimeSlotForOverlapCheck[] {
  return existingSlots.filter(existing => {
    // Ne pas comparer avec soi-même (cas de modification)
    if (newSlot.id && existing.id === newSlot.id) return false;
    
    return hasTimeOverlap(newSlot.start_time, newSlot.end_time, existing.start_time, existing.end_time)
        && hasDaysOverlap(newSlot.available_days, existing.available_days);
  });
}

/**
 * Formate un message d'erreur lisible pour les chevauchements
 */
export function formatOverlapError(overlappingSlots: TimeSlotForOverlapCheck[]): string {
  const slotLabels = overlappingSlots.map(s => 
    `${s.start_time.substring(0, 5)} - ${s.end_time.substring(0, 5)}`
  ).join(', ');
  return `Ce créneau chevauche : ${slotLabels}`;
}

/**
 * Formate les jours concernés par le chevauchement pour un affichage clair
 */
export function formatOverlappingDays(daysA: number[], daysB: number[]): string {
  const DAYS_LABELS = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
  
  // Si l'un des deux est vide, tous les jours sont concernés
  if (daysA.length === 0 || daysB.length === 0) {
    return "tous les jours";
  }
  
  // Trouver les jours communs
  const commonDays = daysA.filter(day => daysB.includes(day));
  return commonDays.map(d => DAYS_LABELS[d]).join(", ");
}
